import { useCallback, useEffect, useRef, useState } from 'react';
import useLabelingStore from '../store/useDetectionStore';
import { attachCanvasOverlay } from '../utils/canvasOverlay';
import { applyViewport, clampZoom, getViewportState } from '../utils/canvasViewport';
import { colorToRgba } from '../utils/colorUtils';
import { getToken, useTheme } from '../../../../../utils/themeTokens';

/**
 * Makes the detection canvas behave like a real image viewer.
 *
 * - the canvas element always fills its container (ResizeObserver), so the
 *   drawing surface is the whole panel rather than the image's pixel box;
 * - wheel zooms about the cursor, shift+wheel pans sideways;
 * - middle-drag and space-drag pan from any tool;
 * - the class-label overlay is attached here so it follows the theme.
 *
 * Everything is wired with native listeners on the container (capture phase for
 * the pan grab) instead of fabric events, so it cannot be torn down by the
 * tool hooks that add/remove their own fabric mouse handlers.
 */
const useCanvasViewport = (canvas, containerRef) => {
  const theme = useTheme();
  const {
    zoomToFit,
    setZoomTo,
    panBy,
    showLabels,
    showFill,
    images,
    currentImageIndex,
  } = useLabelingStore();

  const [cursor, setCursor] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const spaceRef = useRef(false);
  const panRef = useRef(null);

  const currentImage = images?.[currentImageIndex];

  /** Re-apply the current transform after a resize so the image stays framed. */
  const reflow = useCallback(() => {
    if (!canvas) return;
    const { isViewportFitted } = useLabelingStore.getState();
    if (isViewportFitted) {
      zoomToFit();
      return;
    }
    const { zoom, x, y } = getViewportState(canvas);
    applyViewport(canvas, zoom, x, y, useLabelingStore.getState().getCurrentImage());
  }, [canvas, zoomToFit]);

  // ---- canvas element follows the container size -------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    const resize = () => {
      const { width, height } = container.getBoundingClientRect();
      if (width < 1 || height < 1) return;
      if (Math.abs(canvas.getWidth() - width) < 1 && Math.abs(canvas.getHeight() - height) < 1) {
        canvas.calcOffset();
        return;
      }
      canvas.setDimensions({ width: Math.round(width), height: Math.round(height) });
      canvas.calcOffset();
      reflow();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    const recalcOffset = () => canvas.calcOffset();
    window.addEventListener('scroll', recalcOffset, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', recalcOffset, true);
    };
  }, [canvas, containerRef, reflow]);

  // ---- canvas chrome follows the theme -----------------------------------
  useEffect(() => {
    if (!canvas) return;
    const primary = getToken('--primary', '#059669');
    canvas.selectionColor = colorToRgba(primary, 0.12);
    canvas.selectionBorderColor = primary;
    canvas.selectionLineWidth = 1;
    canvas.setBackgroundColor(getToken('--page-bg-subtle', '#eef1f4'), () => canvas.requestRenderAll());
  }, [canvas, theme]);

  // ---- class-name chips + image frame ------------------------------------
  useEffect(() => {
    if (!canvas) return undefined;
    const detach = attachCanvasOverlay(canvas, () => ({
      image: useLabelingStore.getState().getCurrentImage(),
      showLabels: useLabelingStore.getState().showLabels,
    }));
    return detach;
  }, [canvas, theme]);

  // repaint chips when the toggle or the image changes
  useEffect(() => {
    canvas?.requestRenderAll();
  }, [canvas, showLabels, currentImage]);

  // ---- optional box fill (makes very small boxes easier to spot) ----------
  useEffect(() => {
    if (!canvas) return;
    canvas.getObjects().forEach((obj) => {
      const kind = obj?.data?.type;
      const color = obj.classData?.color || obj.stroke;
      if (kind === 'bbox') {
        if (obj.data.isAI) return; // AI suggestions keep their own tint
        obj.set('fill', showFill ? colorToRgba(color, 0.14) : 'transparent');
      } else if (kind === 'polygon') {
        obj.set('fill', colorToRgba(color, showFill ? 0.35 : 0.25));
      }
    });
    canvas.requestRenderAll();
  }, [canvas, showFill, currentImage]);

  // ---- wheel zoom / pan ---------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
      if (event.shiftKey && !event.ctrlKey) {
        // browsers report shift+wheel as deltaX on some platforms, deltaY on others
        panBy(-(event.deltaX || event.deltaY), 0);
        return;
      }
      const rect = canvas.upperCanvasEl.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const { zoom } = getViewportState(canvas);
      // exponential so a trackpad flick and a mouse notch both feel linear
      const next = clampZoom(zoom * Math.exp(-event.deltaY * 0.0022));
      if (Math.abs(next - zoom) < 1e-6) return;
      setZoomTo(next, point);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [canvas, containerRef, panBy, setZoomTo]);

  // ---- space / middle-button panning -------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    // Space must still activate a focused button, and must not eat typing.
    const ownsSpace = (el) =>
      !!el && (['INPUT', 'TEXTAREA', 'BUTTON', 'A', 'SELECT'].includes(el.tagName) || el.isContentEditable);

    const onKeyDown = (event) => {
      if (event.code !== 'Space' || ownsSpace(event.target)) return;
      event.preventDefault();
      if (spaceRef.current) return;
      spaceRef.current = true;
      setIsSpaceHeld(true);
    };
    const onKeyUp = (event) => {
      if (event.code !== 'Space') return;
      spaceRef.current = false;
      setIsSpaceHeld(false);
    };

    const onPointerDown = (event) => {
      const wantsPan = event.button === 1 || (event.button === 0 && spaceRef.current);
      if (!wantsPan) return;
      // capture phase + stopPropagation keeps fabric from starting a selection
      event.preventDefault();
      event.stopPropagation();
      panRef.current = { x: event.clientX, y: event.clientY };
      setIsPanning(true);
      container.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!panRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      panBy(event.clientX - panRef.current.x, event.clientY - panRef.current.y);
      panRef.current = { x: event.clientX, y: event.clientY };
    };

    const endPan = (event) => {
      if (!panRef.current) return;
      panRef.current = null;
      setIsPanning(false);
      container.releasePointerCapture?.(event.pointerId);
    };

    container.addEventListener('pointerdown', onPointerDown, true);
    container.addEventListener('pointermove', onPointerMove, true);
    container.addEventListener('pointerup', endPan, true);
    container.addEventListener('pointerleave', endPan, true);
    const blockAuxClick = (event) => {
      if (event.button === 1) event.preventDefault();
    };
    container.addEventListener('auxclick', blockAuxClick);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown, true);
      container.removeEventListener('pointermove', onPointerMove, true);
      container.removeEventListener('pointerup', endPan, true);
      container.removeEventListener('pointerleave', endPan, true);
      container.removeEventListener('auxclick', blockAuxClick);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      panRef.current = null;
    };
  }, [canvas, containerRef, panBy]);

  // ---- cursor read-out for the status bar --------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    // Coalesced to one update per frame: a setState on every pointermove would
    // re-render the stage mid-drag while the user is drawing a box.
    let frame = 0;
    let pending = null;

    const flush = () => {
      frame = 0;
      setCursor((previous) =>
        previous && previous.x === pending.x && previous.y === pending.y ? previous : pending
      );
    };

    const onMove = (event) => {
      const point = canvas.getPointer(event);
      pending = { x: Math.round(point.x), y: Math.round(point.y) };
      if (!frame) frame = requestAnimationFrame(flush);
    };

    const onLeave = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      setCursor(null);
    };

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }, [canvas, containerRef]);

  return { cursor, isPanning, isSpaceHeld };
};

export default useCanvasViewport;
